import { useMemo } from 'react';
import { useMatches } from 'react-router-dom';

import { APP_NAME } from '@/config/app';
import { usePageTitleOverride } from '@/shared/routes/page-title-context';
import { isInTabTitle, isRouteHandle, segmentLabel } from '@/shared/routes/route-handle';

export interface ResolvedRouteMeta {
  /** Heading for the current page, empty when no matched route declares one. */
  title: string;
  /** Full document title, brand suffix included. */
  documentTitle: string;
  /**
   * The ancestors of the current page, outermost first: `['Admin', 'Lists']`
   * for a list detail. The page's own `title` is NOT included: it is the last
   * crumb, and the TopBar styles it differently.
   *
   * Same source as `documentTitle`, read the other way round. `tabSegments` is
   * declared most-specific-first because that is the order a browser tab wants;
   * a trail reads outermost-first, so it is the reverse and nothing more.
   *
   * `to` is set only for a segment whose handle named a route; see `TabSegment`.
   *
   * Deriving it here is the point. Pages used to hand `PageHeader` a hand-written
   * `breadcrumbs` array, which meant the trail and the tab title were two
   * declarations of the same fact, free to drift, and drifting silently the
   * first time a route was renamed.
   */
  crumbs: readonly { label: string; to?: string }[];
  /** The matched route asked for its heading in the page; see `RouteHandle`. */
  titleInPage: boolean;
}

/**
 * Resolve the current page's title from the matched route chain.
 *
 * The deepest matched route wins, so a nested route can override the section
 * title it sits under. A dynamic override (see `useSetPageTitle`) takes
 * precedence over both, which is how entity pages show a name.
 */
export function useRouteMeta(): ResolvedRouteMeta {
  const matches = useMatches();
  const override = usePageTitleOverride();

  return useMemo(() => {
    const handles = matches.map((match) => match.handle).filter(isRouteHandle);
    const handle = handles.at(-1);

    const title = override ?? handle?.title ?? '';
    const tabSegments = handle?.tabSegments ?? [];
    const labels = tabSegments.filter(isInTabTitle).map(segmentLabel);
    const documentTitle = [title, ...labels, APP_NAME].filter(Boolean).join(' | ');
    const crumbs = [...tabSegments]
      .reverse()
      .map((segment) =>
        typeof segment === 'string'
          ? { label: segment }
          : { label: segment.label, to: segment.to },
      );

    return { title, documentTitle, crumbs, titleInPage: handle?.titleInPage ?? false };
  }, [matches, override]);
}
